<template>
  <div
    class="user-views"
    :class="{ 'can-save': canSave }">
    <drop-down
      class="user-views-main-dropdown"
      ref="userViewsDropdownRef"
      :options="views"
      :icon="IconFilter"
      option-label-property="name"
      option-track-by-property="name"
      :selected-options="selectedView ? [selectedView] : []"
      :select-all-by-default="false"
      :show-select-all="false"
      :is-multiselect="false"
      :tint-when-selections="false"
      tint-class="hollow-success"
      icon-style="width: 1.1rem; height: 1.1rem;"
      :label="capitalizeTranslation('messages.myViews')"
      :mark-as-having-selections-when-all-are-selected="true"
      :return-empty-selected-options-when-all-is-selected="false"
      :use-default-option="false"
      @selection-changed="onViewSelected">
      <template #drop-down-popup-bottom>
        <div
          class="view-management"
          v-if="views?.length">
          <div
            class="action"
            @click="manage">
            <div class="icon">
              <icon-action />
            </div>
            <div class="label">
              {{ capitalizeTranslation('messages.manageMyViews') }}
            </div>
          </div>
        </div>

        <div
          class="action"
          @click="importView">
          <div class="icon">
            <icon-download />
          </div>
          <div class="label">
            {{ capitalizeTranslation('messages.importViews') }}
          </div>
        </div>

        <div
          class="action"
          @click="cleared"
          v-if="views?.length">
          <div class="icon">
            <icon-close />
          </div>
          <div class="label">
            {{ capitalizeTranslation('messages.clearYourView') }}
          </div>
        </div>
      </template>

      <template #noResult>
        <div class="no-views">
          <div class="icon">
            <icon-colored-filter />
          </div>
          <p>{{ $t('messages.viewPlaceholder') }}</p>
        </div>
      </template>
    </drop-down>

    <button
      class="btn btn-sm btn-default btn-no-text save-views"
      :class="{ 'can-save': canSave }"
      data-entity-type="saveView"
      @click="save">
      <icon-save />
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed, useTemplateRef } from 'vue';

  import DropDown from '@/common/components/controls/dropdown/DropDown.vue';
  import UserView from '@/common/components/controls/table/TableUserSettings/UserView';
  import { IconColoredFilter } from '@/common/icons/colored';
  import { IconAction, IconClose, IconDownload, IconFilter, IconSave } from '@/common/icons/monochrome';
  import { capitalizeTranslation } from '@/globals';

  interface IProps {
    views: UserView[];
    canSave: boolean;
  }

  const { canSave = false, views = [] } = defineProps<IProps>();
  const emits = defineEmits(['showViewList', 'cleared', 'viewSelected', 'saved', 'showImportView']);

  const userViewsDropdownRef = useTemplateRef<InstanceType<typeof DropDown>>('userViewsDropdownRef');
  const selectedView = computed(() => views.find((view) => view.isApplied));

  function manage() {
    emits('showViewList');
  }

  function cleared() {
    userViewsDropdownRef.value.clear();
    emits('cleared');
  }

  function onViewSelected(filters: { included: UserView[] }) {
    emits('viewSelected', filters.included[0]);
  }

  function save() {
    emits('saved');
  }

  function importView() {
    emits('showImportView');
  }
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .view-management {
    display: flex;
    flex-direction: column;
    border-top: solid 1px #eee;
    margin-top: 1rem;

    button span {
      text-transform: uppercase;
    }
  }

  .user-views {
    display: flex;
    position: relative;
    transition: all 0.2s ease-in-out;

    &.can-save {
      margin-right: 5rem !important;
      transition: all 0.2s ease-in-out;
    }

    button.list {
      max-width: 20rem;
      overflow: hidden;

      span {
        text-overflow: ellipsis;
        max-width: 15rem;
        overflow: hidden;
      }
    }

    &.can-save {
      button {
        &.list {
          border-right: none;
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }

        &.save {
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
        }
      }
    }
  }

  .action {
    display: grid;
    grid-template-columns: 2rem auto;
    padding: 1rem 2rem;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
      background: #f7f7f7;
      transition: all 0.2s ease-in-out;
      color: var(--primaryColor);

      svg {
        stroke: var(--primaryColor);
      }
    }

    svg {
      height: 1.2rem;
      stroke: var(--textMutedColor);
    }
  }

  .no-views {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px dashed var(--iconColor);
      background: var(--iconColorExtraLight);
      border-radius: 100%;
      height: 10rem;
      width: 10rem;
      margin: 2rem 1rem;

      svg {
        height: 6rem;
      }
    }

    p {
      text-align: center;
    }
  }

  .save-views {
    border-radius: 0 2rem 2rem 0 !important;
    padding-left: 4rem !important;
    padding-right: 2rem !important;
    position: absolute;
    right: 0;
    transition: all 0.2s ease-in-out;

    &.can-save {
      right: -4rem;
      transition: all 0.2s ease-in-out;
    }
  }

  .user-views-main-dropdown {
    z-index: 2;
  }
</style>

<style lang="scss">
  #app .user-views-main-dropdown.drop-down .btn-drop-down .icon {
    margin-right: 0.25rem;
  }

  #app .btn.btn-sm.save-views {
    box-shadow: none;
  }
</style>
